import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Timeline as SummaryIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Assessment as AssessmentIcon,
  Factory as ProductionIcon,
  Science as QualityIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PeriodicSummary = () => {
  const [filters, setFilters] = useState({
    period_type: 'monthly',
    period_value: new Date().toISOString().slice(0, 7), // YYYY-MM format for monthly
    department: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: summaryData, isLoading, error, refetch } = useQuery(
    ['periodicSummary', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/periodic-summary?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching periodic summary: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = async () => {
    const content = document.getElementById('periodic-summary-content');
    if (!content) {
      enqueueSnackbar('No report content to export', { variant: 'warning' });
      return;
    }
    try {
      enqueueSnackbar('Generating PDF...', { variant: 'info' });
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let yPos = 0;
      let remainingHeight = imgHeight;

      // Multi-page support
      while (remainingHeight > 0) {
        if (yPos > 0) pdf.addPage();
        const sourceY = (yPos / imgHeight) * canvas.height;
        const sliceHeight = Math.min(
          ((pageHeight - margin * 2) / imgHeight) * canvas.height,
          canvas.height - sourceY
        );

        // Create a slice canvas for this page
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width;
        pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceImgHeight);

        yPos += pageHeight - margin * 2;
        remainingHeight -= pageHeight - margin * 2;
      }

      const period = formatPeriodValue(filters.period_type, filters.period_value);
      pdf.save(`Periodic_Summary_${period.replace(/\s+/g, '_')}.pdf`);
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (err) {
      console.error('PDF generation error:', err);
      enqueueSnackbar('Error generating PDF', { variant: 'error' });
    }
  };

  const handleDownload = () => {
    handlePrint();
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const formatPeriodValue = (type, value) => {
    if (type === 'monthly') {
      return new Date(value + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
    if (type === 'quarterly') {
      const [year, quarter] = value.split('-Q');
      return `Q${quarter} ${year}`;
    }
    if (type === 'yearly') {
      return value;
    }
    return value;
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading periodic summary: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockSummaryData = {
    period: formatPeriodValue(filters.period_type, filters.period_value),
    production: {
      total_parts_produced: 15420,
      total_tonnage: 285.6,
      ht_treatments: 8960,
      dies_processed: 145,
      trend: 'up',
      previous_period: 14850
    },
    quality: {
      qfbr_count: 8,
      defect_rate: 2.1,
      rework_rate: 3.2,
      customer_complaints: 2,
      trend: 'down',
      previous_period: 2.8
    },
    raw_material: {
      material_received: 320.5,
      srr_processed: 156,
      mtc_received: 89,
      acceptance_rate: 97.8,
      trend: 'up',
      previous_period: 96.2
    },
    efficiency: {
      on_time_delivery: 94.5,
      capacity_utilization: 87.2,
      energy_consumption: 145.8,
      cost_per_ton: 8540,
      trend: 'up',
      previous_period: 92.1
    },
    key_achievements: [
      'Achieved 97.8% raw material acceptance rate',
      'Reduced customer complaints by 60%',
      'Implemented new quality control procedures',
      'Completed preventive maintenance program'
    ],
    areas_for_improvement: [
      'Reduce energy consumption per ton',
      'Improve heat treatment cycle times',
      'Enhance supplier quality ratings',
      'Optimize inventory management'
    ],
    action_items: [
      'Install energy monitoring system by next month',
      'Conduct supplier audit for top 3 suppliers',
      'Review and update HT process parameters',
      'Implement lean manufacturing initiatives'
    ]
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 
      <TrendUpIcon color="success" /> : 
      <TrendDownIcon color="error" />;
  };

  const getTrendColor = (current, previous, higherIsBetter = true) => {
    const isImproved = higherIsBetter ? current > previous : current < previous;
    return isImproved ? 'success.main' : 'error.main';
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SummaryIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Periodic Summary Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Parameters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Period Type</InputLabel>
              <Select
                value={filters.period_type}
                label="Period Type"
                onChange={(e) => {
                  const newType = e.target.value;
                  let newValue = '';
                  if (newType === 'monthly') {
                    newValue = new Date().toISOString().slice(0, 7);
                  } else if (newType === 'quarterly') {
                    const quarter = Math.ceil((new Date().getMonth() + 1) / 3);
                    newValue = `${new Date().getFullYear()}-Q${quarter}`;
                  } else if (newType === 'yearly') {
                    newValue = new Date().getFullYear().toString();
                  }
                  setFilters({ ...filters, period_type: newType, period_value: newValue });
                }}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {filters.period_type === 'monthly' && (
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Month"
                InputLabelProps={{ shrink: true }}
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
            {filters.period_type === 'quarterly' && (
              <TextField
                fullWidth
                size="small"
                label="Quarter (YYYY-QN)"
                placeholder="2024-Q1"
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
            {filters.period_type === 'yearly' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Year"
                value={filters.period_value}
                onChange={(e) => setFilters({ ...filters, period_value: e.target.value })}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department}
                label="Department"
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              >
                <MenuItem value="all">All Departments</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="quality">Quality Control</MenuItem>
                <MenuItem value="raw_material">Raw Material</MenuItem>
                <MenuItem value="heat_treatment">Heat Treatment</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate Summary
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to PDF
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <div id="periodic-summary-content">
          {/* Period Header */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h5" align="center">
              Executive Summary for {mockSummaryData.period}
            </Typography>
          </Paper>

          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ProductionIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="primary">Production</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {mockSummaryData.production.total_parts_produced.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Parts Produced
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(mockSummaryData.production.trend)}
                    <Typography 
                      variant="caption" 
                      sx={{ ml: 0.5 }}
                      color={getTrendColor(
                        mockSummaryData.production.total_parts_produced,
                        mockSummaryData.production.previous_period
                      )}
                    >
                      vs {mockSummaryData.production.previous_period.toLocaleString()} previous
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    Tonnage: {mockSummaryData.production.total_tonnage} MT
                  </Typography>
                  <Typography variant="body2">
                    HT Treatments: {mockSummaryData.production.ht_treatments.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <QualityIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="secondary">Quality</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {mockSummaryData.quality.defect_rate}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Defect Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(mockSummaryData.quality.trend)}
                    <Typography 
                      variant="caption" 
                      sx={{ ml: 0.5 }}
                      color={getTrendColor(
                        mockSummaryData.quality.defect_rate,
                        mockSummaryData.quality.previous_period,
                        false
                      )}
                    >
                      vs {mockSummaryData.quality.previous_period}% previous
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    QFBRs: {mockSummaryData.quality.qfbr_count}
                  </Typography>
                  <Typography variant="body2">
                    Complaints: {mockSummaryData.quality.customer_complaints}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <InventoryIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="success.main">Raw Material</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {mockSummaryData.raw_material.acceptance_rate}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Acceptance Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(mockSummaryData.raw_material.trend)}
                    <Typography 
                      variant="caption" 
                      sx={{ ml: 0.5 }}
                      color={getTrendColor(
                        mockSummaryData.raw_material.acceptance_rate,
                        mockSummaryData.raw_material.previous_period
                      )}
                    >
                      vs {mockSummaryData.raw_material.previous_period}% previous
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    Received: {mockSummaryData.raw_material.material_received} MT
                  </Typography>
                  <Typography variant="body2">
                    SRRs: {mockSummaryData.raw_material.srr_processed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="warning.main">Efficiency</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {mockSummaryData.efficiency.on_time_delivery}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    On-Time Delivery
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(mockSummaryData.efficiency.trend)}
                    <Typography 
                      variant="caption" 
                      sx={{ ml: 0.5 }}
                      color={getTrendColor(
                        mockSummaryData.efficiency.on_time_delivery,
                        mockSummaryData.efficiency.previous_period
                      )}
                    >
                      vs {mockSummaryData.efficiency.previous_period}% previous
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    Capacity: {mockSummaryData.efficiency.capacity_utilization}%
                  </Typography>
                  <Typography variant="body2">
                    Cost/Ton: ₹{mockSummaryData.efficiency.cost_per_ton.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Executive Summary Sections */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="success.main">
                  🎯 Key Achievements
                </Typography>
                <List dense>
                  {mockSummaryData.key_achievements.map((achievement, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color="success" 
                          sx={{ width: 20, height: 20, fontSize: 10 }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={achievement} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="warning.main">
                  🔍 Areas for Improvement
                </Typography>
                <List dense>
                  {mockSummaryData.areas_for_improvement.map((area, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color="warning" 
                          sx={{ width: 20, height: 20, fontSize: 10 }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={area} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="info.main">
                  📋 Action Items
                </Typography>
                <List dense>
                  {mockSummaryData.action_items.map((action, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color="info" 
                          sx={{ width: 20, height: 20, fontSize: 10 }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={action} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Footer */}
          <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="textSecondary" align="center">
              Report generated on {new Date().toLocaleDateString()} •
              KFL MQC System •
              All metrics compared to previous {filters.period_type.slice(0, -2)} period
            </Typography>
          </Paper>
          </div>
        </>
      )}
    </Box>
  );
};

export default PeriodicSummary;